// What the Studio calls its own things in each language: the devices, their
// colours and the built-in games' lines. A table of its own, apart from the
// catalogs (src/core/messages/), so the laptop's apps and Pocket TV, which
// PocketJS bundles, take these few words and not every sentence of the site.
// The snapshot and the command line keep the English names (catalog.ts).

import type { Lang } from "./lang.ts";
import type { DeviceId } from "./types.ts";

/** Each device's name, as the shelf shows it. */
export const DEVICE_NAMES: Record<Lang, Record<DeviceId, string>> = {
  en: { psp: "PSP", vita: "PS Vita", "3ds": "Nintendo 3DS", "ipod-touch": "iPod touch", android: "Android phone" },
  ja: { psp: "PSP", vita: "PS Vita", "3ds": "ニンテンドー3DS", "ipod-touch": "iPod touch", android: "Android スマートフォン" },
};

/** What a short sentence calls a device, where that is less than its name. */
export const DEVICE_SHORT: Record<Lang, Partial<Record<DeviceId, string>>> = {
  en: { "3ds": "3DS" },
  ja: { "3ds": "3DS" },
};

/** Each colour's name, by device and colour id (catalog.ts DEVICES). */
export const SKIN_NAMES: Record<Lang, Record<DeviceId, Record<string, string>>> = {
  en: {
    psp: { "piano-black": "Piano Black", "ceramic-white": "Ceramic White", "felicia-blue": "Felicia Blue", "rose-pink": "Rose Pink", mint: "Mint", champagne: "Champagne" },
    vita: { black: "Black", "glacier-white": "Glacier White", "aqua-blue": "Aqua Blue", lime: "Lime", "neon-orange": "Neon Orange", sakura: "Sakura" },
    "3ds": { "pearl-white": "Pearl White", black: "Black", aqua: "Aqua", "flame-red": "Flame Red", grape: "Grape", sunny: "Sunny" },
    "ipod-touch": { black: "Black", white: "White", pink: "Pink", sky: "Sky", lemon: "Lemon", violet: "Violet" },
    android: { graphite: "Graphite", chalk: "Chalk", coral: "Coral", leaf: "Leaf", cyan: "Cyan", sunflower: "Sunflower" },
  },
  ja: {
    psp: { "piano-black": "ピアノ・ブラック", "ceramic-white": "セラミック・ホワイト", "felicia-blue": "フェリシア・ブルー", "rose-pink": "ローズ・ピンク", mint: "ミント", champagne: "シャンパン" },
    vita: { black: "ブラック", "glacier-white": "グレイシャー・ホワイト", "aqua-blue": "アクア・ブルー", lime: "ライム", "neon-orange": "ネオン・オレンジ", sakura: "サクラ" },
    "3ds": { "pearl-white": "パール・ホワイト", black: "ブラック", aqua: "アクア", "flame-red": "フレア・レッド", grape: "グレープ", sunny: "サニー" },
    "ipod-touch": { black: "ブラック", white: "ホワイト", pink: "ピンク", sky: "スカイ", lemon: "レモン", violet: "バイオレット" },
    android: { graphite: "グラファイト", chalk: "チョーク", coral: "コーラル", leaf: "リーフ", cyan: "シアン", sunflower: "サンフラワー" },
  },
};

/** The built-in games' lines (starters/<id>/studio.json holds the English). */
export const STARTER_TAGLINES: Record<Lang, Record<string, string>> = {
  en: {
    "snack-snake": "Eat snacks, grow long, mind your tail.",
    twenty48: "Slide the tiles, match the numbers, reach 2048.",
    boxel: "Push every box onto its mark.",
  },
  ja: {
    "snack-snake": "おやつを食べるたびに長くなります。自分のしっぽにご注意を。",
    twenty48: "タイルを滑らせ、同じ数を重ねて、2048 を目指すパズル。",
    boxel: "すべての箱を印の上まで押していくパズル。",
  },
};

/** A device's name in `lang`: `PSP`, `Nintendo 3DS` (`ニンテンドー3DS`), `Android phone` (`Android スマートフォン`). */
export function deviceName(id: DeviceId, lang: Lang): string {
  return DEVICE_NAMES[lang][id] ?? DEVICE_NAMES.en[id];
}

/** What a short sentence calls a device in `lang`: `3DS` for the Nintendo 3DS; otherwise its name. */
export function deviceShort(id: DeviceId, lang: Lang): string {
  return DEVICE_SHORT[lang][id] ?? deviceName(id, lang);
}

/** A colour's name in `lang`, by device and colour id; a colour these tables do not know keeps `fallback`. */
export function skinName(device: DeviceId, skin: string, lang: Lang, fallback = skin): string {
  return SKIN_NAMES[lang][device]?.[skin] ?? SKIN_NAMES.en[device]?.[skin] ?? fallback;
}

/** A built-in game's line in `lang`, or null for a game these tables do not know. */
export function starterTagline(app: string, lang: Lang): string | null {
  return STARTER_TAGLINES[lang][app] ?? STARTER_TAGLINES.en[app] ?? null;
}
